<script setup>
import { computed, ref, watch } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { getLastMessage } from 'dashboard/helper/conversationHelper';
import { getCountryCodeFromPhone } from 'dashboard/helper/phoneHelper';
import Avatar from 'next/avatar/Avatar.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import MessagePreview from './MessagePreview.vue';
import InboxName from '../InboxName.vue';
import TimeAgo from 'dashboard/components/ui/TimeAgo.vue';
import CardLabels from './conversationCardComponents/CardLabels.vue';
import CardPriorityIcon from 'dashboard/components-next/Conversation/ConversationCard/CardPriorityIcon.vue';
import SLACardLabel from './components/SLACardLabel.vue';
import VoiceCallStatus from './VoiceCallStatus.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';

const props = defineProps({
  chat: { type: Object, required: true },
  currentContact: { type: Object, required: true },
  assignee: { type: Object, default: () => ({}) },
  inbox: { type: Object, default: () => ({}) },
  selected: { type: Boolean, default: false },
  isActiveChat: { type: Boolean, default: false },
  showAssignee: { type: Boolean, default: false },
  showInboxName: { type: Boolean, default: false },
  hideThumbnail: { type: Boolean, default: false },
  compact: { type: Boolean, default: false },
  isUnassigned: { type: Boolean, default: true },
});

const emit = defineEmits([
  'click',
  'contextmenu',
  'selectConversation',
  'deSelectConversation',
  'acceptTicket',
  'unassignTicket',
]);

const store = useStore();

const hovered = ref(false);

const unreadCount = computed(() => props.chat.unread_count);
const hasUnread = computed(() => unreadCount.value > 0);
const lastMessageInChat = computed(() => getLastMessage(props.chat));

const typingUsers = computed(
  () =>
    store.getters['conversationTypingStatus/getUserList'](props.chat.id) || []
);

const isTyping = computed(() => typingUsers.value.length > 0);

const isAgentTyping = computed(() =>
  typingUsers.value.some(u => u.type !== 'contact')
);

const typingAgentDisplay = computed(() => {
  if (!isTyping.value) return '';
  const agents = typingUsers.value.filter(u => u.type !== 'contact');
  if (agents.length > 0) return agents[0].name;
  return '';
});

const contactCountryCode = computed(() => {
  const phoneNumber = props.currentContact?.phone_number;
  return phoneNumber ? getCountryCodeFromPhone(phoneNumber) : null;
});

const voiceCallData = computed(() => {
  const last = lastMessageInChat.value;
  if (last?.content_type !== 'voice_call' || !last.call) {
    return { status: null, direction: null };
  }
  return {
    status: last.call.status,
    direction: last.call.direction === 'outgoing' ? 'outbound' : 'inbound',
  };
});

const showMetaSection = computed(() => {
  return (
    props.showInboxName ||
    (props.showAssignee && props.assignee.name) ||
    props.chat.priority
  );
});

const isAgentBotAssignee = computed(
  () => props.chat?.meta?.assignee_type === 'AgentBot'
);

const hasSlaPolicyId = computed(
  () => props.chat?.applied_sla?.id && !props.currentContact?.blocked
);

const showLabelsSection = computed(() => {
  return props.chat.labels?.length > 0 || hasSlaPolicyId.value;
});

const messagePreviewClass = computed(() => {
  return [
    hasUnread.value ? 'font-medium text-n-slate-12' : 'text-n-slate-11',
    !props.compact && hasUnread.value ? 'ltr:pr-4 rtl:pl-4' : '',
    props.compact && hasUnread.value ? 'ltr:pr-6 rtl:pl-6' : '',
  ];
});

const onThumbnailHover = () => {
  hovered.value = !props.hideThumbnail;
};

const onThumbnailLeave = () => {
  hovered.value = false;
};

const onSelectConversation = checked => {
  if (checked) {
    emit('selectConversation', props.chat.id, props.inbox.id);
  } else {
    emit('deSelectConversation', props.chat.id, props.inbox.id);
  }
};

const onAcceptTicket = e => {
  e.stopPropagation();
  emit('acceptTicket', e);
};

const onUnassignTicket = e => {
  e.stopPropagation();
  emit('unassignTicket', e);
};

const selectedModel = computed({
  get: () => props.selected,
  set: value => onSelectConversation(value),
});

watch(
  () => props.chat.id,
  () => {
    hovered.value = false;
  }
);
</script>

<template>
  <div
    class="relative flex items-start flex-grow-0 flex-shrink-0 w-auto max-w-full py-0 cursor-pointer conversation border-b border-n-slate-3 hover:border-n-surface-1 hover:bg-n-alpha-1 dark:hover:bg-n-alpha-3 group hover:z-[1] before:content-[none] before:absolute before:-top-px before:inset-x-0 before:h-px before:bg-n-surface-1 before:pointer-events-none hover:before:content-['']"
    :class="{
      'active animate-card-select bg-n-background !border-n-surface-1':
        isActiveChat,
      'selected bg-n-slate-2 !border-n-surface-1': selected,
      'px-0': compact,
      'px-3': !compact,
    }"
    @click="$emit('click', $event)"
    @contextmenu="$emit('contextmenu', $event)"
  >
    <div
      v-if="isTyping"
      class="absolute inset-0 rounded-lg animate-[pulse_2s_ease-in-out_infinite] pointer-events-none"
      :class="isAgentTyping ? '' : 'bg-n-teal-9/25 dark:bg-n-teal-9/15'"
      :style="isAgentTyping ? 'background-color: rgba(59,130,246,0.4)' : ''"
      aria-hidden="true"
    />
    <div
      class="relative"
      @mouseenter="onThumbnailHover"
      @mouseleave="onThumbnailLeave"
    >
      <Avatar
        v-if="!hideThumbnail"
        :name="currentContact.name"
        :src="currentContact.thumbnail"
        :size="40"
        :status="currentContact.availability_status"
        :inbox="inbox"
        :class="!showInboxName ? 'mt-4' : 'mt-8'"
        hide-offline-status
      >
        <template #overlay="{ size }">
          <label
            v-if="hovered || selected"
            class="flex items-center justify-center rounded-full cursor-pointer absolute inset-0 z-10 backdrop-blur-[2px]"
            :style="{ width: `${size}px`, height: `${size}px` }"
            @click.stop
          >
            <Checkbox v-model="selectedModel" />
          </label>
        </template>
      </Avatar>
    </div>
    <div class="px-0 py-3 flex-1 min-w-0 border-line">
      <div
        v-if="showMetaSection"
        class="flex items-center min-w-0 gap-1"
        :class="{
          'ltr:ml-2 rtl:mr-2': !compact,
          'mx-2': compact,
        }"
      >
        <InboxName v-if="showInboxName" :inbox="inbox" class="flex-1 min-w-0" />
        <div
          class="flex items-baseline gap-2 flex-shrink-0"
          :class="{
            'flex-1 justify-between': !showInboxName,
          }"
        >
          <span
            v-if="showAssignee && assignee.name"
            class="text-n-slate-11 text-xs font-medium leading-3 py-0.5 px-0 inline-flex items-center gap-1 truncate"
          >
            <Avatar
              v-if="!isAgentBotAssignee"
              :name="assignee.name"
              :src="assignee.thumbnail"
              :size="14"
              hide-offline-status
              class="flex-shrink-0"
            />
            <Icon
              v-else
              icon="i-lucide-bot"
              class="size-3 text-n-slate-11 flex-shrink-0"
            />
            <span class="truncate">{{ assignee.name }}</span>
          </span>
          <CardPriorityIcon
            :priority="chat.priority"
            class="flex-shrink-0 !size-3.5"
          />
        </div>
      </div>
      <h4
        class="conversation--user text-sm my-0 mx-2 capitalize pt-0.5 text-ellipsis overflow-hidden whitespace-nowrap flex-1 min-w-0 ltr:pr-24 rtl:pl-24 text-n-slate-12 flex items-center gap-1.5"
        :class="hasUnread ? 'font-semibold' : 'font-medium'"
      >
        <span
          v-if="contactCountryCode"
          :class="`fi fi-${contactCountryCode}`"
          class="flex-shrink-0 w-4 h-3 rounded-sm"
          :title="contactCountryCode.toUpperCase()"
        />
        <span class="truncate">
          {{ currentContact.name }}
        </span>
      </h4>
      <VoiceCallStatus
        v-if="voiceCallData.status"
        key="voice-status-row"
        :status="voiceCallData.status"
        :direction="voiceCallData.direction"
        :message-preview-class="messagePreviewClass"
      />
      <MessagePreview
        v-else-if="lastMessageInChat"
        key="message-preview"
        :message="lastMessageInChat"
        class="my-0 mx-2 leading-6 h-6 flex-1 min-w-0 text-sm"
        :class="[messagePreviewClass, isTyping ? 'ltr:pr-20 rtl:pl-20' : '']"
      />
      <p
        v-else
        key="no-messages"
        class="text-n-slate-11 text-sm my-0 mx-2 leading-6 h-6 flex-1 min-w-0 overflow-hidden text-ellipsis whitespace-nowrap"
        :class="messagePreviewClass"
      >
        <fluent-icon
          size="16"
          class="-mt-0.5 align-middle inline-block text-n-slate-10"
          icon="info"
        />
        <span class="mx-0.5">
          {{ $t(`CHAT_LIST.NO_MESSAGES`) }}
        </span>
      </p>
      <div
        class="absolute flex flex-col ltr:right-3 rtl:left-3 gap-1"
        :class="showMetaSection ? 'top-8' : 'top-4'"
      >
        <span class="ml-auto font-normal leading-4 text-xxs">
          <TimeAgo
            :last-activity-timestamp="chat.timestamp"
            :created-at-timestamp="chat.created_at"
            :conversation-id="chat.id"
          />
        </span>
        <span
          v-if="!isTyping && hasUnread"
          class="shadow-lg rounded-full text-xxs font-semibold h-4 leading-4 min-w-[1rem] px-1 py-0 text-center text-white bg-n-teal-9 ltr:ml-auto rtl:mr-auto mt-1"
        >
          {{ unreadCount }}
        </span>
        <span
          v-if="isTyping"
          class="flex gap-1 items-center justify-end mt-1 h-5 px-2 py-0.5 rounded-full"
          :class="isAgentTyping ? 'bg-blue-50' : 'bg-n-teal-2'"
        >
          <fluent-icon
            :icon="isAgentTyping ? 'person' : 'edit'"
            size="12"
            :class="isAgentTyping ? 'text-blue-700' : 'text-n-teal-9'"
          />
          <span
            class="text-xxs font-medium"
            :class="isAgentTyping ? 'text-blue-700' : 'text-n-teal-9'"
            >{{
              isAgentTyping && typingAgentDisplay
                ? typingAgentDisplay
                : $t('CHAT_LIST.TYPING')
            }}
          </span>
          <span class="flex gap-0.5 items-center">
            <span
              class="w-1 h-1 rounded-full animate-bounce"
              :style="
                isAgentTyping
                  ? 'background-color: #1d4ed8; animation-delay: -0.3s'
                  : 'animation-delay: -0.3s'
              "
              :class="isAgentTyping ? '' : 'bg-n-teal-9'"
            />
            <span
              class="w-1 h-1 rounded-full animate-bounce"
              :style="
                isAgentTyping
                  ? 'background-color: #1d4ed8; animation-delay: -0.15s'
                  : 'animation-delay: -0.15s'
              "
              :class="isAgentTyping ? '' : 'bg-n-teal-9'"
            />
            <span
              class="w-1 h-1 rounded-full animate-bounce"
              :style="isAgentTyping ? 'background-color: #1d4ed8' : ''"
              :class="isAgentTyping ? '' : 'bg-n-teal-9'"
            />
          </span>
        </span>
      </div>
      <!-- Accept/Unassign buttons - positioned at bottom-right to avoid overlap -->
      <div
        class="absolute bottom-3 ltr:right-3 rtl:left-3 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
      >
        <button
          v-if="!isUnassigned"
          class="shadow-lg rounded-full text-xxs font-semibold h-6 leading-6 min-w-[3.5rem] px-3 flex items-center justify-center text-white bg-n-teal-9 hover:bg-n-teal-10 transition-colors duration-200"
          @click="onUnassignTicket"
        >
          {{ $t('CHAT_LIST.UNASSIGN_TICKET') || 'Devolver' }}
        </button>
        <button
          v-if="isUnassigned"
          class="shadow-lg rounded-full text-xxs font-semibold h-6 leading-6 min-w-[3rem] px-3 flex items-center justify-center text-white bg-n-teal-9 hover:bg-n-teal-10 transition-colors duration-200"
          @click="onAcceptTicket"
        >
          {{ $t('CHAT_LIST.ACCEPT_TICKET') }}
        </button>
      </div>
      <CardLabels
        v-if="showLabelsSection"
        :conversation-labels="chat.labels"
        class="mt-0.5 mx-2 mb-0"
      >
        <template v-if="hasSlaPolicyId" #before>
          <SLACardLabel :chat="chat" class="ltr:mr-1 rtl:ml-1" />
        </template>
      </CardLabels>
    </div>
  </div>
</template>

<style>
@import 'flag-icons/css/flag-icons.min.css';
</style>
